/**
 * fieldLimits.js — single source of truth for input character limits.
 *
 * Two tiers (see submission_vs_case_logic.md §6):
 *  - HARD limits mirror the fixed-width NVARCHAR columns in the `cases` table
 *    (migrations 001/007). Exceeding one causes a real SQL save error, so the
 *    UI hard-caps via the native `maxLength` attribute.
 *  - SOFT limits guard NVARCHAR(MAX)-backed narratives against pathological
 *    pastes. The counter warns past the limit but never blocks (we never
 *    truncate a clinical narrative).
 *
 * Keys are the FRONTEND field names used in the wizard `form` object and the
 * case-edit drafts (both surfaces import from here), NOT the SQL column names.
 */

// Generous soft cap for every NVARCHAR(MAX)-backed narrative field. Arbitrary —
// large enough never to be hit in practice, small enough to stop a runaway paste.
export const SOFT_NARRATIVE_LIMIT = 8000;

// Fixed-width columns → HARD cap.
export const HARD_LIMITS = {
  Title:                        200, // client_name / incident title
  ClientPhoneNumber:            50,  // client_phone
  Clinic:                       200,
  Region:                       120,
  Country:                      60,
  ServiceCarriedOut:            200,
  StaffMemberConcerned:         200,
  ClinicManagerResponsible:     200,
  MedicalTeamMemberResponsible: 200,
  IncidentCategoryOther:        500, // incident_category_other
  TreatmentSubtypeOther:        60,  // reuses the treatment_subtype column
  // NearMiss / ClinicIncident free-text that maps to incident_category_other
  NM_CategoryOther:             500,
  CI_IncidentTypeOther:         500,
};

// Free-text living inside the type_specific JSON bag (no per-field DB limit) →
// sensible HARD caps. Keyed by both the wizard form key and the stored bag key
// where they differ.
export const TYPE_SPECIFIC_LIMITS = {
  // Treatment matrix extras (FIELD_META snake_case keys)
  trainer_name:        200,
  laser_settings:      200,
  laser_serial_number: 100,
  // AWI optional detail (all codes) — required_evidence_logic.md §6
  product_used:        200,
  area_treated:        200,
  dosage:              100,
  // Consent-form waiver reason (type_specific.waivers.consent_form.reason);
  // the server bounds it at 500 too (required_evidence_logic.md §3).
  consent_waiver_reason: 500,
  // Upload metadata (attachments.metadata.trainerName, ≤200 server-side)
  trainerName:         200,
  // NearMiss / ClinicIncident bag keys (camelCase)
  assignee:            200, // NM "Assigned To" / NM_AssigneeOther
  injuredName:         200,
  injuredContact:      200,
  witnessName:         200,
  witnessContact:      200,
  // NearMiss / ClinicIncident wizard form keys (before remap)
  NM_AssigneeOther:    200,
  CI_InjuredName:      200,
  CI_InjuredContact:   200,
  CI_WitnessName:      200,
  CI_WitnessContact:   200,
};

// NVARCHAR(MAX)-backed narratives → SOFT cap (counter warns, never blocks).
// Keyed by wizard form keys AND stored bag keys.
export const SOFT_LIMITS = {
  // AWI optional multi-line detail (type_specific.symptoms) — soft 2000
  symptoms:            2000,
  // incident_description (immutable) / medical_notes / case_resolution columns
  IncidentDescription: SOFT_NARRATIVE_LIMIT,
  MedicalNotes:     SOFT_NARRATIVE_LIMIT,
  newNote:          SOFT_NARRATIVE_LIMIT,
  caseResolution:   SOFT_NARRATIVE_LIMIT,
  // NearMiss narratives (wizard form keys)
  NM_Description:      SOFT_NARRATIVE_LIMIT,
  NM_Cause:            SOFT_NARRATIVE_LIMIT,
  NM_CorrectiveAction: SOFT_NARRATIVE_LIMIT,
  // NearMiss narratives (stored bag keys)
  cause:            SOFT_NARRATIVE_LIMIT,
  correctiveAction: SOFT_NARRATIVE_LIMIT,
  // Refusal Register narratives (wizard form keys + stored bag keys)
  RR_BuyerDescription:     SOFT_NARRATIVE_LIMIT,
  RR_ActionTaken:          SOFT_NARRATIVE_LIMIT,
  refusalBuyerDescription: SOFT_NARRATIVE_LIMIT,
  refusalActionTaken:      SOFT_NARRATIVE_LIMIT,
  // ClinicIncident narratives (wizard form keys)
  CI_Description:        SOFT_NARRATIVE_LIMIT,
  CI_InjuryNature:       SOFT_NARRATIVE_LIMIT,
  CI_ImmediateResponse:  SOFT_NARRATIVE_LIMIT,
  CI_WitnessStatement:   SOFT_NARRATIVE_LIMIT,
  // ClinicIncident narratives (stored bag keys)
  injuryNature:      SOFT_NARRATIVE_LIMIT,
  immediateResponse: SOFT_NARRATIVE_LIMIT,
  witnessStatement:  SOFT_NARRATIVE_LIMIT,
};

/**
 * Resolve the limit for a field key.
 * @returns {{ max: number, hard: boolean } | null} null when the field has no
 *   limit (selects, dates, checkboxes, or any unlisted field).
 */
export function fieldLimit(key) {
  if (key in HARD_LIMITS)          return { max: HARD_LIMITS[key],          hard: true };
  if (key in TYPE_SPECIFIC_LIMITS) return { max: TYPE_SPECIFIC_LIMITS[key], hard: true };
  if (key in SOFT_LIMITS)          return { max: SOFT_LIMITS[key],          hard: false };
  return null;
}
